import { BaseElement } from 'base';
import { bus } from 'bus';

const POSITIONS = ['top', 'bottom', 'left', 'right'];
const ARROWS = ['start', 'center', 'end'];
const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const PERPENDICULAR = {
  top: ['right', 'left'],
  bottom: ['right', 'left'],
  left: ['bottom', 'top'],
  right: ['bottom', 'top']
};
const DEFAULT_OPEN_DELAY = 100;
const DEFAULT_CLOSE_DELAY = 200;
const FALLBACK_LENGTHS = {
  '--tooltip-viewport-margin': 8,
  '--tooltip-arrow-inset': 48,
  '--tooltip-arrow-inset-block': 24,
  '--tooltip-arrow-min-offset': 24
};

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const delayOf = (raw, fallback) => {
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : fallback;
};

class InfoTooltip extends BaseElement {
  #handlers = new Set();
  #trigger = null;
  #panel = null;
  #content = null;
  #closeButton = null;
  #layer = 'float';
  #position = 'top';
  #arrow = 'start';
  #flip = true;
  #event = 'hover';
  #openDelay = DEFAULT_OPEN_DELAY;
  #closeDelay = DEFAULT_CLOSE_DELAY;
  #timer = 0;
  #pointerTimer = 0;
  #frame = 0;
  #lastSize = null;
  #pointerOpened = false;
  #pointerInside = false;
  #suppressFocusOpen = false;
  #reconciling = false;
  #opening = false;
  #activeId = null;

  onConnect() {
    this.#pointerOpened = false;
    this.#pointerInside = false;
    this.#pointerTimer = 0;
    this.#suppressFocusOpen = false;
    this.#opening = false;
    this.#lastSize = null;
    this.#activeId = null;

    this.#trigger = this.q('.tooltip__trigger');
    this.#panel = this.q('.tooltip__panel');
    if (!this.#trigger || !this.#panel) return;

    this.#content = this.q('.tooltip__content');
    this.#closeButton = this.q('.tooltip__close');

    if (typeof this.#panel.showPopover !== 'function')
      this.dataset.layer = 'inline';

    this.#layer = this.dataset.layer === 'inline' ? 'inline' : 'float';
    this.#position = POSITIONS.includes(this.dataset.position)
      ? this.dataset.position
      : 'top';
    this.#arrow = ARROWS.includes(this.dataset.arrow)
      ? this.dataset.arrow
      : 'start';
    this.#flip = this.dataset.flip !== 'false';
    this.#event = this.dataset.event === 'click' ? 'click' : 'hover';
    this.#openDelay = delayOf(this.dataset.openDelay, DEFAULT_OPEN_DELAY);
    this.#closeDelay = delayOf(this.dataset.closeDelay, DEFAULT_CLOSE_DELAY);

    this.#reconciling = true;
    this.subscribe('tooltip:active', this.#onActiveTooltip);
    this.#reconcile();
    this.#reconciling = false;

    this.listen(window, 'resize', this.#schedule);
    this.listen(window, 'scroll', this.#schedule, {
      passive: true,
      capture: true
    });
    this.observe(
      new ResizeObserver(entries => this.#onPanelResize(entries)),
      this.#panel
    );

    this.listen(this, 'pointerenter', this.#onPointerEnter);
    this.listen(this, 'pointerleave', this.#onPointerLeave);
    this.listen(this, 'focusin', this.#onFocusIn);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.listen(this, 'click', this.#onClick);

    this.listen(document, 'pointerdown', this.#onDocumentPointerdown, {
      capture: true
    });
    this.listen(document, 'pointerup', this.#onDocumentPointerEnd, {
      capture: true
    });
    this.listen(document, 'pointercancel', this.#onDocumentPointerEnd, {
      capture: true
    });
    if (this.#layer !== 'inline') {
      this.listen(document, 'keydown', this.#onDocumentKeydown, {
        capture: true
      });
    }

    this.cleanup(() => {
      clearTimeout(this.#timer);
      clearTimeout(this.#pointerTimer);
      cancelAnimationFrame(this.#frame);
    });
  }

  onDisconnect() {
    this.#handlers.clear();
    if (this.#layer === 'float' && this.#activeId === this.id)
      bus.publish('tooltip:active', null);
  }

  get isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  get disabled() {
    return this.#trigger?.getAttribute('aria-disabled') === 'true';
  }

  set disabled(value) {
    if (!this.#trigger) return;
    if (value) {
      this.#trigger.setAttribute('aria-disabled', 'true');
      this.dataset.disabled = 'true';
      this.close({ reason: 'api' });
      return;
    }
    this.#trigger.removeAttribute('aria-disabled');
    delete this.dataset.disabled;
  }

  open({ reason = 'api' } = {}) {
    if (this.disabled || this.isOpen) return;
    this.#showPanel();
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#opening = true;
    try {
      this.place();
    } finally {
      this.#opening = false;
    }
    if (!this.isOpen) return;
    this.#syncOverflowTabindex();
    this.#publishActive();
    this.#emit({ id: this.id, open: true, reason });
  }

  close({ reason = 'api', restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.#clearTimer();
    this.#pointerOpened = false;
    this.#trigger.setAttribute('aria-expanded', 'false');

    this.#suppressFocusOpen = true;
    this.#hidePanel();
    if (restoreFocus && this.isConnected) this.#trigger.focus();
    queueMicrotask(() => {
      this.#suppressFocusOpen = false;
    });

    if (!this.#opening) this.#emit({ id: this.id, open: false, reason });

    if (this.#layer === 'float' && this.#activeId === this.id)
      bus.publish('tooltip:active', null);
  }

  toggle({ reason = 'api' } = {}) {
    if (this.isOpen) this.close({ reason });
    else this.open({ reason });
  }

  onToggle(handler) {
    this.#handlers.add(handler);
    return () => this.#handlers.delete(handler);
  }

  place() {
    if (this.#layer !== 'float' || !this.isOpen) return;

    const panel = this.#panel;
    const trigger = this.#trigger;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const triggerRect = trigger.getBoundingClientRect();

    if (
      triggerRect.bottom <= 0 ||
      triggerRect.top >= viewportHeight ||
      triggerRect.right <= 0 ||
      triggerRect.left >= viewportWidth
    ) {
      this.close({ reason: 'outside' });
      return;
    }

    const computedStyle = getComputedStyle(this);
    const num = name => {
      const value = parseFloat(computedStyle.getPropertyValue(name));
      if (Number.isFinite(value)) return value;
      console.error(
        `[tooltip] ${name} did not resolve to a length; falling back to ${FALLBACK_LENGTHS[name]}px`
      );
      return FALLBACK_LENGTHS[name];
    };
    const margin = num('--tooltip-viewport-margin');
    const insetInline = num('--tooltip-arrow-inset');
    const insetBlock = num('--tooltip-arrow-inset-block');
    const minOffset = num('--tooltip-arrow-min-offset');
    const rtl = computedStyle.direction === 'rtl';

    panel.style.left = '0px';
    panel.style.top = '0px';

    const boxes = {};
    const boxFor = candidate => {
      const mode =
        candidate === 'top' || candidate === 'bottom' ? 'top' : 'left';
      if (!boxes[mode]) {
        panel.dataset.position = mode;
        const rect = panel.getBoundingClientRect();
        boxes[mode] = { width: rect.width, height: rect.height };
      }
      return boxes[mode];
    };
    const need = candidate =>
      candidate === 'top' || candidate === 'bottom'
        ? boxFor(candidate).height
        : boxFor(candidate).width;

    const room = {
      top: triggerRect.top - margin,
      bottom: viewportHeight - triggerRect.bottom - margin,
      left: triggerRect.left - margin,
      right: viewportWidth - triggerRect.right - margin
    };

    let side = this.#position;
    if (this.#flip && room[side] < need(side)) {
      const opposite = OPPOSITE[side];
      if (room[opposite] >= need(opposite)) {
        side = opposite;
      } else {
        const candidates = PERPENDICULAR[side]
          .slice()
          .sort(
            (first, second) =>
              room[second] - need(second) - (room[first] - need(first))
          );
        side =
          candidates.find(candidate => room[candidate] >= need(candidate)) ??
          candidates[0];
      }
    }

    const panelBox = boxFor(side);
    const horizontal = side === 'top' || side === 'bottom';
    const size = horizontal ? panelBox.width : panelBox.height;
    const inset = Math.min(horizontal ? insetInline : insetBlock, size / 2);
    const aMin = Math.min(minOffset, size / 2);
    const aMax = size - aMin;
    const limit = horizontal ? viewportWidth : viewportHeight;

    let slotName = this.#arrow;
    if (rtl && horizontal) {
      if (slotName === 'start') slotName = 'end';
      else if (slotName === 'end') slotName = 'start';
    }
    const slot =
      slotName === 'start'
        ? inset
        : slotName === 'end'
          ? size - inset
          : size / 2;

    const tip = horizontal
      ? triggerRect.left + triggerRect.width / 2
      : triggerRect.top + triggerRect.height / 2;
    let lead = clamp(tip - slot, margin, limit - size - margin);
    if (tip - lead < aMin) lead = Math.max(0, tip - aMin);
    if (tip - lead > aMax) lead = Math.min(limit - size, tip - aMax);

    const arrowOffset = clamp(tip - lead, aMin, aMax);

    let main = 0;
    if (side === 'top') main = triggerRect.top - panelBox.height;
    if (side === 'bottom') main = triggerRect.bottom;
    if (side === 'left') main = triggerRect.left - panelBox.width;
    if (side === 'right') main = triggerRect.right;
    main = clamp(
      main,
      margin,
      (horizontal
        ? viewportHeight - panelBox.height
        : viewportWidth - panelBox.width) - margin
    );

    panel.style.left = `${horizontal ? lead : main}px`;
    panel.style.top = `${horizontal ? main : lead}px`;
    panel.style.setProperty('--tooltip-arrow-offset', `${arrowOffset}px`);
    panel.dataset.position = side;
    panel.dataset.arrow = slotName;

    this.#syncOverflowTabindex();

    const after = panel.getBoundingClientRect();
    this.#lastSize = { w: after.width, h: after.height };
  }

  #canPopover() {
    return (
      this.#layer === 'float' && typeof this.#panel.showPopover === 'function'
    );
  }

  #showPanel() {
    this.#panel.hidden = false;
    if (this.#canPopover() && !this.#panel.matches(':popover-open'))
      this.#panel.showPopover();
  }

  #hidePanel() {
    if (this.#canPopover() && this.#panel.matches(':popover-open'))
      this.#panel.hidePopover();
    this.#panel.hidden = true;
  }

  #publishActive() {
    if (this.#layer !== 'float') return;
    bus.publish('tooltip:active', this.id);
  }

  #reconcile() {
    const wantOpen =
      this.#trigger.getAttribute('aria-expanded') === 'true' && !this.disabled;
    if (wantOpen) {
      this.#showPanel();
      this.#publishActive();
      this.#schedule();
      this.#syncOverflowTabindex();
      return;
    }
    this.#hidePanel();
    this.#trigger.setAttribute('aria-expanded', 'false');
  }

  #onActiveTooltip(activeId) {
    this.#activeId = activeId;
    if (this.#reconciling) return;
    if (this.#layer !== 'float') return;
    if (activeId !== this.id && this.isOpen) this.close({ reason: 'outside' });
  }

  #syncOverflowTabindex() {
    const content = this.#content;
    if (!content) return;
    if (content.scrollHeight > content.clientHeight + 1)
      content.setAttribute('tabindex', '0');
    else content.removeAttribute('tabindex');
  }

  #onPanelResize(entries) {
    this.#syncOverflowTabindex();
    const box = entries[0]?.contentRect;
    if (!box || !this.#lastSize) {
      this.#schedule();
      return;
    }
    if (
      Math.abs(box.width - this.#lastSize.w) < 0.5 &&
      Math.abs(box.height - this.#lastSize.h) < 0.5
    )
      return;
    this.#schedule();
  }

  #schedule() {
    if (!this.isOpen || this.#layer !== 'float') return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.place());
  }

  #after(delay, callback) {
    this.#clearTimer();
    this.#timer = setTimeout(callback, delay);
  }

  #clearTimer() {
    clearTimeout(this.#timer);
    this.#timer = 0;
  }

  #keyboardFocused() {
    try {
      return this.#trigger.matches(':focus-visible');
    } catch {
      return false;
    }
  }

  #onPointerEnter(event) {
    if (
      event.pointerType !== 'mouse' ||
      this.disabled ||
      this.#event !== 'hover'
    )
      return;
    if (this.isOpen) {
      this.#clearTimer();
      this.#pointerOpened = true;
      return;
    }
    this.#after(this.#openDelay, () => this.open({ reason: 'pointer' }));
  }

  #onPointerLeave(event) {
    if (event.pointerType !== 'mouse' || this.#event !== 'hover') return;
    this.#pointerOpened = false;
    if (!this.isOpen) {
      this.#clearTimer();
      return;
    }
    this.#after(this.#closeDelay, () => this.close({ reason: 'outside' }));
  }

  #onFocusIn(event) {
    if (this.#suppressFocusOpen) {
      this.#suppressFocusOpen = false;
      return;
    }
    if (this.#event !== 'hover' || this.disabled) return;
    if (!this.#trigger.contains(event.target)) return;
    if (!this.#keyboardFocused()) return;
    this.open({ reason: 'focus' });
  }

  #onFocusOut(event) {
    if (!this.isOpen) return;
    const next = event.relatedTarget;
    if (next) {
      if (!this.contains(next)) this.close({ reason: 'blur' });
      return;
    }
    if (this.#pointerInside || !document.hasFocus()) return;
    this.close({ reason: 'blur' });
  }

  #onClick(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    if (this.#closeButton && this.#closeButton.contains(event.target)) {
      this.close({ reason: 'dismiss', restoreFocus: true });
      return;
    }
    if (!this.#trigger.contains(event.target)) return;
    this.#clearTimer();
    if (this.#pointerOpened) {
      this.#pointerOpened = false;
      return;
    }
    this.toggle({ reason: 'click' });
  }

  #onDocumentPointerdown(event) {
    clearTimeout(this.#pointerTimer);
    this.#pointerInside = this.contains(event.target);
    if (this.#layer === 'inline') return;
    if (this.isOpen && !this.#pointerInside) this.close({ reason: 'outside' });
  }

  #onDocumentPointerEnd() {
    clearTimeout(this.#pointerTimer);
    this.#pointerTimer = setTimeout(() => {
      this.#pointerInside = false;
    }, 0);
  }

  #onDocumentKeydown(event) {
    if (event.key !== 'Escape' || this.#layer === 'inline' || !this.isOpen)
      return;
    event.stopPropagation();
    this.close({
      reason: 'escape',
      restoreFocus: this.contains(document.activeElement)
    });
  }

  #emit(detail) {
    for (const handler of [...this.#handlers]) {
      try {
        handler(detail);
      } catch (error) {
        console.error('[tooltip] toggle handler failed:', error);
      }
    }
  }
}

if (!customElements.get('info-tooltip'))
  customElements.define('info-tooltip', InfoTooltip);
